<script setup lang="ts">
import {
  Copy as CopyIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  PenLine as PenLineIcon,
  Route as RouteIcon,
  SquareMinus as SquareMinusIcon,
  Trash2 as Trash2Icon,
} from '@lucide/vue'
import { computed } from 'vue'

import type { ActionMenuItem } from '@/components/ui/actionMenu'
import ActionMenuList from '@/components/ui/ActionMenuList.vue'
import { buildTourPrimaryActionItems } from '@/composables/useTourPrimaryActionItems'
import type { Tour } from '@/types/tour'

defineOptions({ name: 'WorkspaceTourActionSheetContent' })

const props = defineProps<{
  tour: Tour
  canEdit: boolean
  canRemove: boolean
  canDelete: boolean
  isActivity: boolean
  isVisible: boolean
  isDownloading?: boolean
}>()

const emit = defineEmits<{
  rename: []
  download: []
  addToCollection: []
  togglePinned: []
  toggleVisibility: []
  editRoute: []
  duplicateOrEditAsRoute: []
  remove: []
  delete: []
}>()

const actionItems = computed<ActionMenuItem[]>(() => {
  const items: ActionMenuItem[] = buildTourPrimaryActionItems({
    tour: props.tour,
    canEdit: props.canEdit,
    isActivity: props.isActivity,
    isDownloading: props.isDownloading,
    onTogglePinned: () => emit('togglePinned'),
    onEditRoute: () => emit('editRoute'),
    onDownload: () => emit('download'),
    onAddToCollection: () => emit('addToCollection'),
  })

  items.push({
    key: 'toggle-visibility',
    label: props.isVisible ? 'Hide' : 'Unhide',
    icon: props.isVisible ? EyeOffIcon : EyeIcon,
    onSelect: () => emit('toggleVisibility'),
  })

  if (props.canEdit) {
    items.push({
      key: 'duplicate-or-edit-as-route',
      label: props.isActivity ? 'Edit as route' : 'Duplicate',
      icon: props.isActivity ? RouteIcon : CopyIcon,
      onSelect: () => emit('duplicateOrEditAsRoute'),
    })

    items.push({
      key: 'rename',
      label: 'Rename',
      icon: PenLineIcon,
      onSelect: () => emit('rename'),
    })
  }

  if (props.canRemove) {
    items.push({
      key: 'remove',
      label: 'Remove from collection',
      icon: SquareMinusIcon,
      danger: true,
      onSelect: () => emit('remove'),
    })
  }

  if (props.canDelete) {
    items.push({
      key: 'delete',
      label: 'Delete',
      icon: Trash2Icon,
      danger: true,
      separated: !props.canRemove,
      onSelect: () => emit('delete'),
    })
  }

  return items
})
</script>

<template>
  <ActionMenuList :items="actionItems" />
</template>
